import React, { useState, useEffect } from "react";
import axios from "axios";
import { API } from "../api";
import { useAuth } from "../App";

function MiniBar({ value, max, color }) {
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
      <div style={{ flex: 1, height: 8, background: "#f0f0f0", borderRadius: 4, overflow: "hidden" }}>
        <div style={{ width: `${Math.min(100, max > 0 ? (value / max) * 100 : 0)}%`, height: "100%", background: color, borderRadius: 4, transition: "width 0.4s" }} />
      </div>
      <span style={{ fontSize: 12, color: "#555", width: 36, textAlign: "right" }}>{value}j</span>
    </div>
  );
}

function LineChart({ data, label, color }) {
  const max = Math.max(...data.map(d => d.value), 1);
  const h = 100;
  const w = 420;
  const pts = data.map((d, i) => {
    const x = (i / (data.length - 1)) * w;
    const y = h - (d.value / max) * h;
    return `${x},${y}`;
  }).join(" ");
  return (
    <div>
      <div style={{ fontSize: 11, color: "#888", marginBottom: 8, fontWeight: 500, textTransform: "uppercase" }}>{label}</div>
      <div style={{ overflowX: "auto" }}>
        <svg width={w} height={h + 24} style={{ display: "block" }}>
          {/* Grille */}
          {[0, 0.5, 1].map((p, i) => (
            <line key={i} x1="0" y1={h - p * h} x2={w} y2={h - p * h} stroke="#f0f0f0" strokeWidth="1" />
          ))}
          {/* Ligne */}
          {data.length > 1 && (
            <polyline points={pts} fill="none" stroke={color} strokeWidth="2.5" strokeLinejoin="round" strokeLinecap="round" />
          )}
          {/* Points */}
          {data.map((d, i) => {
            const x = (i / (data.length - 1)) * w;
            const y = h - (d.value / max) * h;
            return (
              <g key={i}>
                <circle cx={x} cy={y} r={4} fill={color} />
                {d.value > 0 && <text x={x} y={y - 8} textAnchor="middle" fontSize="10" fill="#666">{d.value}</text>}
                <text x={x} y={h + 16} textAnchor="middle" fontSize="10" fill="#aaa">{d.label}</text>
              </g>
            );
          })}
        </svg>
      </div>
    </div>
  );
}

function DonutSmall({ slices, size = 80 }) {
  const r = (size / 2) - 8;
  const circ = 2 * Math.PI * r;
  const total = slices.reduce((s, sl) => s + sl.value, 0);
  let offset = 0;
  return (
    <svg width={size} height={size}>
      <circle cx={size/2} cy={size/2} r={r} fill="none" stroke="#f0f0f0" strokeWidth="12" />
      {slices.filter(sl => sl.value > 0).map((sl, i) => {
        const dash = (sl.value / total) * circ;
        const el = (
          <circle key={i} cx={size/2} cy={size/2} r={r} fill="none"
            stroke={sl.color} strokeWidth="12"
            strokeDasharray={`${dash} ${circ - dash}`}
            strokeDashoffset={circ / 4 - offset}
          />
        );
        offset += dash;
        return el;
      })}
    </svg>
  );
}

function Statistics() {
  const { token } = useAuth();
  const [data, setData] = useState(null);
  const [yearFilter, setYearFilter] = useState(new Date().getFullYear());

  useEffect(() => {
    axios.get(API + "/leaves/stats", {
      headers: { Authorization: "Bearer " + token }
    }).then(r => setData(r.data)).catch(err => console.error(err));
  }, [token]);

  if (!data) return <div style={{ fontSize: 13, color: "#888", padding: 32 }}>Chargement...</div>;

  const leaves = data.leaves.filter(l => new Date(l.date_debut).getFullYear() === yearFilter);

  // Jours posés par mois
  const months = Array.from({ length: 12 }, (_, i) => {
    const label = new Date(yearFilter, i).toLocaleDateString("fr-FR", { month: "short" });
    const value = leaves.filter(l => new Date(l.date_debut).getMonth() === i)
      .reduce((s, l) => s + parseFloat(l.jours || 0), 0);
    return { label, value: Math.round(value * 10) / 10 };
  });

  // Par type (dynamique selon les données)
  const palette = ["#7C6FE0","#185FA5","#3B6D11","#854F0B","#A32D2D","#5C8A8A","#8A5C8A"];
  const typeSet = [...new Set(leaves.map(l => l.type))].filter(Boolean);
  const byType = typeSet.map((t, i) => ({
    label: t, color: palette[i % palette.length],
    value: Math.round(leaves.filter(l => l.type === t).reduce((s, l) => s + parseFloat(l.jours || 0), 0) * 10) / 10
  }));

  // Par équipe (associé)
  const teams = {};
  for (const l of leaves) {
    const key = l.associe_prenom ? `${l.associe_prenom} ${l.associe_nom}` : "Sans équipe";
    teams[key] = (teams[key] || 0) + parseFloat(l.jours || 0);
  }
  const teamList = Object.entries(teams).map(([k, v]) => ({ label: k, value: Math.round(v * 10) / 10 }))
    .sort((a, b) => b.value - a.value);
  const maxTeam = Math.max(...teamList.map(t => t.value), 1);

  // Taux utilisation par collaborateur
  const collabs = data.users.map(u => {
    const total = parseFloat(u.cp_total) || 30;
    const poses = leaves.filter(l => l.user_id === u.id).reduce((s, l) => s + parseFloat(l.jours || 0), 0);
    const taux = Math.round((poses / total) * 100);
    return { ...u, poses: Math.round(poses * 10) / 10, total, taux };
  }).sort((a, b) => b.taux - a.taux);

  const totalJours = Math.round(leaves.reduce((s, l) => s + parseFloat(l.jours || 0), 0) * 10) / 10;

  return (
    <div>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 20 }}>
        <h2 style={{ margin: 0, fontWeight: 500 }}>Statistiques cabinet</h2>
        <select value={yearFilter} onChange={e => setYearFilter(parseInt(e.target.value))}
          style={{ padding: "7px 12px", border: "1px solid #ddd", borderRadius: 6, fontSize: 13 }}>
          {[2024, 2025, 2026, 2027].map(y => <option key={y} value={y}>{y}</option>)}
        </select>
      </div>

      {/* KPIs */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))", gap: 12, marginBottom: 20 }}>
        {[
          { label: "Jours posés total", value: totalJours, color: "#7C6FE0", bg: "#EEEDFE" },
          { label: "Congés enregistrés", value: leaves.length, color: "#185FA5", bg: "#E6F1FB" },
          { label: "Moy. par collab", value: data.users.length > 0 ? Math.round(totalJours / data.users.length * 10) / 10 : 0, color: "#3B6D11", bg: "#EAF3DE" },
        ].map(s => (
          <div key={s.label} style={{ background: s.bg, borderRadius: 10, padding: "16px 20px", borderLeft: `4px solid ${s.color}` }}>
            <div style={{ fontSize: 11, color: s.color, marginBottom: 4, fontWeight: 500, textTransform: "uppercase" }}>{s.label}</div>
            <div style={{ fontSize: 26, fontWeight: 700, color: "#333" }}>{s.value}</div>
          </div>
        ))}
      </div>

      {/* Ligne mensuelle */}
      <div style={{ background: "#fff", border: "1px solid #e0e0e0", borderRadius: 10, padding: 20, marginBottom: 16 }}>
        <LineChart data={months} label={`Jours posés par mois — ${yearFilter}`} color="#7C6FE0" />
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16, marginBottom: 16 }}>
        {/* Répartition par type */}
        <div style={{ background: "#fff", border: "1px solid #e0e0e0", borderRadius: 10, padding: 20 }}>
          <div style={{ fontSize: 11, color: "#888", marginBottom: 16, fontWeight: 500, textTransform: "uppercase" }}>Répartition par type</div>
          <div style={{ display: "flex", alignItems: "center", gap: 20 }}>
            <DonutSmall slices={byType} size={90} />
            <div style={{ flex: 1 }}>
              {byType.filter(t => t.value > 0).map(t => (
                <div key={t.label} style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8 }}>
                  <span style={{ width: 10, height: 10, background: t.color, borderRadius: 2, flexShrink: 0 }}></span>
                  <span style={{ fontSize: 12, color: "#555", flex: 1 }}>{t.label}</span>
                  <span style={{ fontSize: 12, fontWeight: 600, color: "#333" }}>{t.value}j</span>
                </div>
              ))}
              {byType.every(t => t.value === 0) && <div style={{ fontSize: 12, color: "#aaa" }}>Aucune donnée</div>}
            </div>
          </div>
        </div>

        {/* Par équipe */}
        <div style={{ background: "#fff", border: "1px solid #e0e0e0", borderRadius: 10, padding: 20 }}>
          <div style={{ fontSize: 11, color: "#888", marginBottom: 16, fontWeight: 500, textTransform: "uppercase" }}>Jours posés par équipe</div>
          {teamList.length === 0 ? <div style={{ fontSize: 12, color: "#aaa" }}>Aucune donnée</div> : teamList.map(t => (
            <div key={t.label} style={{ marginBottom: 10 }}>
              <div style={{ display: "flex", justifyContent: "space-between", fontSize: 12, marginBottom: 3 }}>
                <span style={{ color: "#555" }}>{t.label}</span>
              </div>
              <MiniBar value={t.value} max={maxTeam} color="#185FA5" />
            </div>
          ))}
        </div>
      </div>

      {/* Taux utilisation par collab */}
      <div style={{ background: "#fff", border: "1px solid #e0e0e0", borderRadius: 10, padding: 20 }}>
        <div style={{ fontSize: 11, color: "#888", marginBottom: 16, fontWeight: 500, textTransform: "uppercase" }}>Taux d'utilisation par collaborateur</div>
        {collabs.length === 0 ? <div style={{ fontSize: 12, color: "#aaa" }}>Aucune donnée</div> : (
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
            <thead><tr>
              {["Collaborateur", "Dotation", "Posés", "Restants", "Utilisation"].map(h => (
                <th key={h} style={{ textAlign: "left", padding: "6px 10px", fontSize: 11, color: "#888", borderBottom: "1px solid #eee" }}>{h}</th>
              ))}
            </tr></thead>
            <tbody>
              {collabs.map(c => (
                <tr key={c.id}>
                  <td style={td}><b>{c.prenom} {c.nom}</b></td>
                  <td style={td}>{c.total}j</td>
                  <td style={td}>{c.poses}j</td>
                  <td style={td}><span style={{ color: (c.total - c.poses) <= 5 ? "#A32D2D" : "#3B6D11", fontWeight: 600 }}>{Math.round((c.total - c.poses) * 10) / 10}j</span></td>
                  <td style={{ ...td, minWidth: 160 }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                      <div style={{ flex: 1, height: 8, background: "#f0f0f0", borderRadius: 4, overflow: "hidden" }}>
                        <div style={{ width: `${Math.min(100, c.taux)}%`, height: "100%", background: c.taux > 80 ? "#A32D2D" : c.taux > 50 ? "#854F0B" : "#7C6FE0", borderRadius: 4 }} />
                      </div>
                      <span style={{ fontSize: 12, color: "#555", width: 36 }}>{c.taux}%</span>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

const td = { padding: "8px 10px", borderBottom: "1px solid #f5f5f5" };

export default Statistics;
